/* eslint-disable object-curly-newline, react/jsx-curly-newline */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { stringify } from 'qs';
import {
  Table,
  Button,
  Tooltip,
  Input,
  Popconfirm,
  Spin,
  message,
  Switch,
  Collapse,
  Modal,
} from 'antd';
// eslint-disable-next-line import/no-extraneous-dependencies
import {
  PlusCircleOutlined,
  InfoCircleOutlined,
  CloseOutlined,
  RightOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import { verifyFeatureAccess } from '../../../global/featureFlags';
import { captureAnalytics } from '../../../actions/preload';
import {
  getCategoryCatalog,
  enableSupportedCategory,
  disableSupportedCategory,
  updateCategory,
  addClassificationHint,
  removeClassificationHint,
  setTitleInstruction,
  setTitleOptions,
} from '../../../actions/categories';
import './manage-categories.scss';
import '../../modal/settings-modal.scss';

const { Panel } = Collapse;

// rc-table wraps custom expandIcon in a span that calls stopPropagation when
// expandRowByClick is true (see rc-table/hooks/useColumns.js). The caret must
// therefore call onExpand itself — otherwise the wrapper eats the click and
// row expansion never fires. The row's own onClick still handles clicks on
// the rest of the row.
const ExpandToggle = ({ expanded, record, onExpand }) => (
  <span
    role="button"
    aria-label={expanded ? 'Collapse row' : 'Expand row'}
    onClick={(e) => onExpand(record, e)}
    onKeyDown={(e) => {
      if (e.key === 'Enter' || e.key === ' ') onExpand(record, e);
    }}
    tabIndex={0}
    style={{
      display: 'inline-flex',
      transform: expanded ? 'rotate(90deg)' : 'none',
      transition: 'transform 120ms ease',
      padding: 4,
      color: '#8a8a8a',
      cursor: 'pointer',
    }}
  >
    <RightOutlined />
  </span>
);

ExpandToggle.propTypes = {
  expanded: PropTypes.bool.isRequired,
  record: PropTypes.object.isRequired,
  onExpand: PropTypes.func.isRequired,
};
const { Search, TextArea } = Input;
const ALL_CUSTOM_CATEGORIES_QUERY = '?no_pagination=true';
// Keep in sync with DocumentCategoriesController::TITLE_EXAMPLE_MAX_LENGTH
// and TITLE_INSTRUCTION_MAX_LENGTH.
const TITLE_EXAMPLE_MAX_LENGTH = 150;
const TITLE_INSTRUCTION_MAX_LENGTH = 500;

// Custom titling only takes effect when the AI-service titling pipeline runs
// (PatientInformationExtractionWorker#apply_title_generation gates
// TitleStrategyResolver/DocumentTitleService on this org feature). Without it,
// saved instructions would silently do nothing — so the editor is only shown
// when the org actually has this flag on.
const AI_SERVICE_TITLING_FEATURE = 'ai_service_titling';
// Title options (per-category document titles) only do anything when document
// title classification is enabled for the org, so the editor is shown only
// when this flag is on.
const DOCUMENT_TITLE_CLASSIFICATION_FEATURE = 'document_title_classification';
// Keep in sync with DocumentCategoriesController::TITLE_OPTIONS_MAX_COUNT
// and TITLE_OPTION_MAX_LENGTH.
const TITLE_OPTIONS_MAX_COUNT = 100;
const TITLE_OPTION_MAX_LENGTH = 100;
const stringifyOptions = {
  format: 'RFC1738',
  addQueryPrefix: true,
  sort: (a, b) => (a.localeCompare(b)),
};

class ManageCategories extends Component {
  constructor(props) {
    super(props);
    this.state = {
      categories: null,
      currentPage: 1,
      searchValue: '',
      catalogSearch: '',
      canManageCategory: false,
      catalogEnabled: null,
      titlingEnabled: false,
      titleClassificationEnabled: false,
      emrEdits: {},
      hintDrafts: {},
      hintsExpanded: {},
      titlingDrafts: {},
      titlingExpanded: {},
      titleOptionDrafts: {},
      titleOptionsExpanded: {},
      titleOptionsSaving: {},
      expandedRowKeys: [],
    };
  }

  async componentDidMount() {
    const { getUserInfo, fetchCategoryCatalog } = this.props;
    await getUserInfo();
    const [canManageCategory, catalogEnabled, titlingEnabled, titleClassificationEnabled] = await Promise.all([
      verifyFeatureAccess('manage_category'),
      verifyFeatureAccess('global_ai_category_catalog'),
      verifyFeatureAccess(AI_SERVICE_TITLING_FEATURE),
      verifyFeatureAccess(DOCUMENT_TITLE_CLASSIFICATION_FEATURE),
    ]);

    if (!catalogEnabled) {
      await this.loadLegacyCategories();
      this.setState({
        categories: this.props.data,
        currentPage: 1,
        canManageCategory,
        catalogEnabled: false,
      });
      return;
    }

    await Promise.all([
      this.refreshCustomCategories(),
      fetchCategoryCatalog(),
    ]);

    this.setState({
      categories: this.props.data,
      canManageCategory,
      catalogEnabled,
      titlingEnabled,
      titleClassificationEnabled,
    });
  }

  componentDidUpdate(prevProps) {
    if (prevProps.data !== this.props.data) {
      this.setState({ categories: this.props.data });
    }
  }

  refreshCustomCategories = async () => {
    const { getCategoriesByQuery } = this.props;
    await getCategoriesByQuery(ALL_CUSTOM_CATEGORIES_QUERY);
  };

  loadLegacyCategories = async ({ page = 1, searchValue = '' } = {}) => {
    const { getCategoriesByQuery } = this.props;
    const query = stringify(
      {
        page,
        ...(searchValue.trim() ? { contains: searchValue.trim() } : {}),
      },
      stringifyOptions,
    );
    await getCategoriesByQuery(query);
  };

  getRefreshQuery = () => {
    const { catalogEnabled, currentPage, searchValue } = this.state;

    if (catalogEnabled) return ALL_CUSTOM_CATEGORIES_QUERY;

    return stringify(
      {
        page: currentPage,
        ...(searchValue.trim() ? { contains: searchValue.trim() } : {}),
      },
      stringifyOptions,
    );
  };

  handleLegacyPageChange = async (page) => {
    const { searchValue } = this.state;
    this.setState({ currentPage: page });
    await this.loadLegacyCategories({ page, searchValue });
  };

  handleLegacyTableChange = ({ current }) => this.handleLegacyPageChange(current);

  searchLegacyCategories = async (value) => {
    this.setState({ searchValue: value, currentPage: 1 });
    await this.loadLegacyCategories({ page: 1, searchValue: value });
  };

  handleEmrInputChange = (documentCategoryId, value) => {
    this.setState((prev) => ({
      emrEdits: { ...prev.emrEdits, [documentCategoryId]: value },
    }));
  };

  commitEmrCategory = async (entry) => {
    const { updateCustomCategory, fetchCategoryCatalog } = this.props;
    const documentCategoryId = entry.organization_state.document_category_id;
    if (!documentCategoryId) return;

    const { emrEdits } = this.state;
    const pending = emrEdits[documentCategoryId];
    if (pending === undefined) return;

    const trimmed = pending.trim();
    if (!trimmed || trimmed === entry.organization_state.emr_category) {
      this.setState((prev) => {
        const next = { ...prev.emrEdits };
        delete next[documentCategoryId];
        return { emrEdits: next };
      });
      return;
    }

    const response = await updateCustomCategory(documentCategoryId, { emr_category: trimmed });

    if (response?.error) {
      const errMessage = response.response?.data?.message
        || response.response?.data?.error
        || 'Failed to update EMR category';
      message.error(errMessage);
      return;
    }

    this.setState((prev) => {
      const next = { ...prev.emrEdits };
      delete next[documentCategoryId];
      return { emrEdits: next };
    });
    await fetchCategoryCatalog();
  };

  persistToggleSupported = async (entry) => {
    const { enableSupported, disableSupported, fetchCategoryCatalog } = this.props;
    const currentlyEnabled = entry.organization_state.enabled;
    const response = currentlyEnabled
      ? await disableSupported(entry.id)
      : await enableSupported(entry.id);

    if (response?.error) {
      const errMessage = response.response?.data?.message
        || response.response?.data?.error
        || 'Failed to update category';
      message.error(errMessage);
      return;
    }

    await Promise.all([
      this.refreshCustomCategories(),
      fetchCategoryCatalog(),
    ]);
  };

  handleToggleSupported = (entry) => {
    const { organization_state: orgState } = entry;

    if (orgState.enabled && orgState.ever_used) {
      Modal.confirm({
        className: 'settings-modal-wrap',
        title: 'Disable this AI category?',
        content: (
          <span>
            Medsender has already used
            {' '}
            <strong>{entry.display_name}</strong>
            {' '}
            for this organization. Future incoming documents will no longer be
            auto-classified into this category, but existing records will not change.
          </span>
        ),
        okText: 'Disable category',
        okType: 'danger',
        cancelText: 'Cancel',
        onOk: () => this.persistToggleSupported(entry),
      });
      return;
    }

    this.persistToggleSupported(entry);
  };

  // Draft-input change/clear handlers shared by the per-category list editors
  // (hintDrafts, titleOptionDrafts) and the titling editor (titlingDrafts).
  handleListDraftChange = (draftsKey, documentCategoryId, value) => {
    this.setState((prev) => ({
      [draftsKey]: { ...prev[draftsKey], [documentCategoryId]: value },
    }));
  };

  clearListDraft = (draftsKey, documentCategoryId) => {
    this.setState((prev) => {
      const next = { ...prev[draftsKey] };
      delete next[documentCategoryId];
      return { [draftsKey]: next };
    });
  };

  submitHint = async (entry) => {
    const { addHint } = this.props;
    const documentCategoryId = entry.organization_state.document_category_id;
    if (!documentCategoryId) return;

    const draft = (this.state.hintDrafts[documentCategoryId] || '').trim();
    if (!draft) return;

    const response = await addHint(documentCategoryId, draft);

    if (response?.error) {
      const errMessage = response.response?.data?.error
        || response.response?.data?.message
        || 'Failed to add hint';
      message.error(errMessage);
      return;
    }

    this.clearListDraft('hintDrafts', documentCategoryId);
  };

  removeHint = async (entry, index) => {
    const { removeHint } = this.props;
    const documentCategoryId = entry.organization_state.document_category_id;
    if (!documentCategoryId) return;

    const response = await removeHint(documentCategoryId, index);

    if (response?.error) {
      const errMessage = response.response?.data?.error
        || response.response?.data?.message
        || 'Failed to remove hint';
      message.error(errMessage);
    }
  };

  openEditCategoryModal = (category = null) => {
    const { openModal, isAdmin } = this.props;
    const selectedCategory = category && category.currentTarget ? null : category;
    return openModal({
      type: 'ADD_CATEGORY',
      data: {
        category: selectedCategory,
        isAdmin,
        refreshQuery: this.getRefreshQuery(),
      },
    });
  };

  deleteCategory = async (category) => {
    const { deleteCategory } = this.props;
    const response = await deleteCategory(category.id);

    if (response?.error) {
      if (response.response?.status === 403) {
        message.error('Cannot delete category. You need permission to perform this action');
      } else if (response.response?.status === 409) {
        message.error(
          response.response?.data?.error
            || 'Cannot delete a catalog-backed category',
        );
      } else {
        message.error('Failed to delete category');
      }
      return;
    }

    const { catalogEnabled, currentPage, searchValue } = this.state;

    if (catalogEnabled) {
      await this.refreshCustomCategories();
      return;
    }

    await this.loadLegacyCategories({ page: currentPage, searchValue });
  };

  setExpandedRowKeys = (keys) => this.setState({ expandedRowKeys: keys });

  toggleHintsExpanded = (dcId) => {
    this.setState((prev) => ({
      hintsExpanded: { ...prev.hintsExpanded, [dcId]: !prev.hintsExpanded[dcId] },
    }));
  };

  toggleTitlingExpanded = (dcId) => {
    this.setState((prev) => {
      const nextOpen = !prev.titlingExpanded[dcId];
      const nextDrafts = { ...prev.titlingDrafts };
      if (!nextOpen) delete nextDrafts[dcId];

      return {
        titlingDrafts: nextDrafts,
        titlingExpanded: { ...prev.titlingExpanded, [dcId]: nextOpen },
      };
    }, () => {
      if (this.state.titlingExpanded[dcId]) {
        captureAnalytics('category_custom_titling_editor_opened', { document_category_id: dcId });
      }
    });
  };

  // Reads the current value of one titling field (example|description), falling
  // back to the saved value when the field hasn't been edited this session.
  titlingFieldValue = (entry, field) => {
    const dcId = entry.organization_state.document_category_id;
    const draft = dcId ? this.state.titlingDrafts[dcId] : undefined;
    if (draft && draft[field] !== undefined) return draft[field];
    const savedKey = field === 'example' ? 'title_example' : 'title_instruction';
    return entry.organization_state[savedKey] || '';
  };

  handleTitlingDraftChange = (documentCategoryId, field, value) => {
    this.setState((prev) => ({
      titlingDrafts: {
        ...prev.titlingDrafts,
        [documentCategoryId]: { ...prev.titlingDrafts[documentCategoryId], [field]: value },
      },
    }));
  };

  commitTitling = async (entry) => {
    const { saveTitleInstruction } = this.props;
    const documentCategoryId = entry.organization_state.document_category_id;
    if (!documentCategoryId) return;

    const titleExample = this.titlingFieldValue(entry, 'example').trim();
    const titleInstruction = this.titlingFieldValue(entry, 'description').trim();

    const response = await saveTitleInstruction(documentCategoryId, { titleExample, titleInstruction });

    if (response?.error) {
      const errMessage = response.response?.data?.error
        || response.response?.data?.message
        || 'Failed to save custom titling';
      message.error(errMessage);
      return;
    }

    this.clearListDraft('titlingDrafts', documentCategoryId);
    const configured = !!(titleExample || titleInstruction);
    captureAnalytics(
      configured ? 'category_custom_titling_saved' : 'category_custom_titling_cleared',
      { document_category_id: documentCategoryId },
    );
    message.success(configured ? 'Custom titling saved' : 'Custom titling cleared');
  };

  removeTitling = async (entry) => {
    const { saveTitleInstruction } = this.props;
    const documentCategoryId = entry.organization_state.document_category_id;
    if (!documentCategoryId) return;

    const response = await saveTitleInstruction(documentCategoryId, { titleExample: '', titleInstruction: '' });

    if (response?.error) {
      const errMessage = response.response?.data?.error
        || response.response?.data?.message
        || 'Failed to remove custom titling';
      message.error(errMessage);
      return;
    }

    this.clearListDraft('titlingDrafts', documentCategoryId);
    captureAnalytics('category_custom_titling_cleared', { document_category_id: documentCategoryId });
    message.success('Custom titling cleared');
  };

  toggleTitleOptionsExpanded = (dcId) => {
    this.setState((prev) => {
      const nextOpen = !prev.titleOptionsExpanded[dcId];
      return {
        titleOptionsExpanded: { ...prev.titleOptionsExpanded, [dcId]: nextOpen },
      };
    }, () => {
      if (this.state.titleOptionsExpanded[dcId]) {
        captureAnalytics('category_title_options_editor_opened', { document_category_id: dcId });
      }
    });
  };

  setTitleOptionsSaving = (dcId, saving) => {
    this.setState((prev) => ({
      titleOptionsSaving: { ...prev.titleOptionsSaving, [dcId]: saving },
    }));
  };

  // Title options are a full-list replace on the backend, so add/remove both
  // recompute the whole array from the category's current title_options. That
  // makes concurrent saves for the same category last-write-wins: a second
  // save computed from the pre-save list would silently undo the first. The
  // titleOptionsSaving flag serializes them — the editor's controls are
  // disabled while a save is in flight, and the handlers bail as a backstop.
  submitTitleOption = async (entry) => {
    const { saveTitleOptions } = this.props;
    const documentCategoryId = entry.organization_state.document_category_id;
    if (!documentCategoryId) return;
    if (this.state.titleOptionsSaving[documentCategoryId]) return;

    const draft = (this.state.titleOptionDrafts[documentCategoryId] || '').trim();
    if (!draft) return;

    const current = entry.organization_state.title_options || [];
    if (current.some((title) => title.toLowerCase() === draft.toLowerCase())) {
      message.warning('That title option already exists');
      return;
    }

    this.setTitleOptionsSaving(documentCategoryId, true);
    let response;
    try {
      response = await saveTitleOptions(documentCategoryId, [...current, draft]);
    } finally {
      this.setTitleOptionsSaving(documentCategoryId, false);
    }

    if (response?.error) {
      const errMessage = response.response?.data?.error
        || response.response?.data?.message
        || 'Failed to add title option';
      message.error(errMessage);
      return;
    }

    this.clearListDraft('titleOptionDrafts', documentCategoryId);
    captureAnalytics('category_title_option_added', {
      document_category_id: documentCategoryId,
      title_option_count: current.length + 1,
    });
  };

  removeTitleOption = async (entry, index) => {
    const { saveTitleOptions } = this.props;
    const documentCategoryId = entry.organization_state.document_category_id;
    if (!documentCategoryId) return;
    if (this.state.titleOptionsSaving[documentCategoryId]) return;

    const current = entry.organization_state.title_options || [];
    const next = current.filter((_, idx) => idx !== index);

    this.setTitleOptionsSaving(documentCategoryId, true);
    let response;
    try {
      response = await saveTitleOptions(documentCategoryId, next);
    } finally {
      this.setTitleOptionsSaving(documentCategoryId, false);
    }

    if (response?.error) {
      const errMessage = response.response?.data?.error
        || response.response?.data?.message
        || 'Failed to remove title option';
      message.error(errMessage);
      return;
    }

    captureAnalytics('category_title_option_removed', {
      document_category_id: documentCategoryId,
      title_option_count: next.length,
    });
  };

  filteredCatalog = () => {
    const items = this.props.catalog?.items || [];
    const q = this.state.catalogSearch.trim().toLowerCase();
    if (!q) return items;
    return items.filter((entry) => (
      entry.display_name.toLowerCase().includes(q)
      || entry.slug.toLowerCase().includes(q)
      || (entry.description || '').toLowerCase().includes(q)
      || (entry.organization_state.emr_category || '').toLowerCase().includes(q)
    ));
  };

  renderSummaryBar = (items) => {
    const enabledCount = items.filter((e) => e.organization_state.enabled).length;
    const hiddenCount = items.filter((e) => e.hidden).length;
    const hintedCount = items.filter(
      (e) => (e.organization_state.classification_hints || []).length > 0,
    ).length;

    const enabledLabel = `${enabledCount} of ${items.length} enabled`;
    const hiddenLabel = `${hiddenCount} hidden (custom for you)`;
    const hintsLabel = `${hintedCount} with hints`;

    return (
      <div className="manage-categories__summary-bar">
        <div className="manage-categories__counts">
          <span className="manage-categories__count-chip manage-categories__count-chip--enabled">
            <span className="dot" />
            {enabledLabel}
          </span>
          {hiddenCount > 0 && (
            <span className="manage-categories__count-chip manage-categories__count-chip--hidden">
              <span className="dot" />
              {hiddenLabel}
            </span>
          )}
          <span className="manage-categories__count-chip manage-categories__count-chip--hints">
            <span className="dot" />
            {hintsLabel}
          </span>
        </div>
        <Search
          allowClear
          className="manage-categories__search"
          placeholder="Search categories…"
          value={this.state.catalogSearch}
          onChange={(e) => this.setState({ catalogSearch: e.target.value })}
        />
      </div>
    );
  };

  renderRowName = (entry) => {
    const { description, organization_state: orgState } = entry;
    const tooltip = description ? (
      <div style={{ maxWidth: 360 }}>{description}</div>
    ) : null;

    const nameClass = orgState.enabled
      ? 'manage-categories__row-name'
      : 'manage-categories__row-name manage-categories__row-name--off';
    const hintCount = (orgState.classification_hints || []).length;

    const hintLabel = `${hintCount} ${hintCount === 1 ? 'hint' : 'hints'}`;
    const hasCustomTitling = !!(orgState.title_example || orgState.title_instruction);

    return (
      <div className="manage-categories__row">
        <span className={nameClass}>{entry.display_name}</span>
        {tooltip && (
          <Tooltip title={tooltip} placement="right">
            <InfoCircleOutlined style={{ color: '#888' }} />
          </Tooltip>
        )}
        {entry.protected && <span className="manage-categories__pill manage-categories__pill--required">Required</span>}
        {entry.hidden && <span className="manage-categories__pill manage-categories__pill--hidden">Custom for you</span>}
        {orgState.ever_used && <span className="manage-categories__pill manage-categories__pill--in-use">In use</span>}
        {hintCount > 0 && (
          <span className="manage-categories__pill manage-categories__pill--hints">{hintLabel}</span>
        )}
        {hasCustomTitling && (
          <span className="manage-categories__pill manage-categories__pill--titling">Custom title</span>
        )}
      </div>
    );
  };

  renderEmrReadonly = (entry) => {
    if (!entry.organization_state.enabled) {
      return <span className="manage-categories__row-meta">—</span>;
    }
    const value = entry.organization_state.emr_category;
    if (!value) {
      return <span className="manage-categories__row-meta"><em>not set</em></span>;
    }
    return (
      <Tooltip title="The exact value Medsender will pick in your EMR's category dropdown. Click the row to edit.">
        <span className="manage-categories__emr-readonly">{value}</span>
      </Tooltip>
    );
  };

  // Shared progressive-disclosure editor for a per-category list of short
  // strings (classification hints, title options): a summary line with a
  // quiet expand/cancel toggle, a removable item list, and an add row bounded
  // by maxCount/maxLength. `saving` disables the add/remove controls while a
  // save is in flight, for lists persisted as a full-list replace.
  renderListEditor = ({
    dcId, items, open, draft, readOnly, keyPrefix,
    summary, toggleLabel, removeAriaLabel, placeholder,
    maxCount, maxLength, maxedNoun,
    onToggle, onDraftChange, onSubmit, onRemove,
    saving = false,
  }) => (
    <div className="manage-categories__hints-footer">
      <div className="manage-categories__hints-summary">
        {summary}
        {!readOnly && items.length === 0 && (
          <button
            type="button"
            className="manage-categories__hints-toggle"
            onClick={onToggle}
          >
            {open ? 'Cancel' : toggleLabel}
          </button>
        )}
      </div>

      {open && (
        <div className="manage-categories__hints">
          {items.length > 0 && (
            <ul className="manage-categories__hint-list">
              {items.map((item, idx) => (
                <li
                  // eslint-disable-next-line react/no-array-index-key
                  key={`${dcId}-${keyPrefix}-${idx}`}
                  className="manage-categories__hint-item"
                >
                  <span className="manage-categories__hint-item-text">{item}</span>
                  {!readOnly && (
                    <button
                      type="button"
                      aria-label={removeAriaLabel}
                      disabled={saving}
                      onClick={() => onRemove(idx)}
                    >
                      <CloseOutlined />
                    </button>
                  )}
                </li>
              ))}
            </ul>
          )}
          {!readOnly && items.length < maxCount && (
            <div className="manage-categories__hint-input-row">
              <Input
                className="manage-categories__hint-input"
                size="small"
                placeholder={placeholder}
                value={draft}
                maxLength={maxLength}
                showCount
                onChange={(e) => onDraftChange(e.target.value)}
                onPressEnter={onSubmit}
              />
              <Button
                size="small"
                type="primary"
                disabled={!draft.trim() || saving}
                loading={saving}
                onClick={onSubmit}
              >
                Add
              </Button>
            </div>
          )}
          {!readOnly && items.length >= maxCount && (
            <div className="manage-categories__hint-help">
              {`Maximum of ${maxCount} ${maxedNoun} reached. Remove one to add another.`}
            </div>
          )}
        </div>
      )}
    </div>
  );

  renderDetailPanel = (entry) => {
    const {
      canManageCategory, emrEdits, hintDrafts, hintsExpanded,
      titlingExpanded, titlingEnabled,
      titleClassificationEnabled, titleOptionDrafts, titleOptionsExpanded,
      titleOptionsSaving,
    } = this.state;
    const { isAdmin } = this.props;
    const readOnly = canManageCategory && !isAdmin;
    const { organization_state: orgState } = entry;
    const { document_category_id: dcId, enabled } = orgState;
    const pendingEmr = dcId ? emrEdits[dcId] : undefined;
    const emrValue = pendingEmr !== undefined
      ? pendingEmr
      : (orgState.emr_category || '');
    const hints = orgState.classification_hints || [];
    const draft = dcId ? (hintDrafts[dcId] || '') : '';
    // Auto-expand the hints editor when hints already exist; otherwise hidden
    // behind a quiet "Add a hint" link so the section doesn't compete with
    // the primary description + EMR mapping.
    const hintsOpen = enabled && (hints.length > 0 || !!hintsExpanded[dcId]);

    const savedTitleExample = orgState.title_example || '';
    const savedTitleInstruction = orgState.title_instruction || '';
    const titlingConfigured = savedTitleExample.length > 0 || savedTitleInstruction.length > 0;
    // Eligible refill categories feed their generated title into task de-duplication.
    // Warn because a custom format can make the medication + dose unparseable.
    const dedupTitleCategory = !!orgState.task_dedup_eligible;
    const exampleValue = this.titlingFieldValue(entry, 'example');
    const descriptionValue = this.titlingFieldValue(entry, 'description');
    // Same progressive-disclosure pattern as hints: auto-open when titling is
    // already configured, otherwise hide behind a quiet link so this advanced
    // control doesn't clutter the panel for the common case.
    const titlingOpen = enabled && (titlingConfigured || !!titlingExpanded[dcId]);
    const titlingDirty = exampleValue.trim() !== savedTitleExample
      || descriptionValue.trim() !== savedTitleInstruction;

    // Title options: per-category list of document titles the AI picks from
    // (manual uploads offer them as suggestions but still accept free text).
    // Same progressive-disclosure pattern as classification hints.
    const titleOptions = orgState.title_options || [];
    const titleOptionDraft = dcId ? (titleOptionDrafts[dcId] || '') : '';
    const titleOptionsOpen = enabled && (titleOptions.length > 0 || !!titleOptionsExpanded[dcId]);

    return (
      <div className="manage-categories__detail">
        <div className="manage-categories__detail-block">
          <h4>What documents land here</h4>
          <div className="manage-categories__description">
            {entry.description || 'No description.'}
          </div>
        </div>

        <div className="manage-categories__detail-block">
          <h4>Your EMR Category</h4>
          <div className="manage-categories__field-help">
            When the AI classifies a document into
            {' '}
            <strong>{entry.display_name}</strong>
            , Medsender selects this exact value from your EMR&apos;s category dropdown
            during auto-upload. Case-sensitive.
          </div>
          {enabled ? (
            (() => {
              const savedEmr = orgState.emr_category || '';
              const trimmed = (pendingEmr ?? '').trim();
              const hasPendingChange = pendingEmr !== undefined
                && trimmed.length > 0
                && trimmed !== savedEmr;
              return (
                <div className="manage-categories__emr-edit-row">
                  <Input
                    className="manage-categories__emr-edit"
                    size="small"
                    value={emrValue}
                    placeholder='e.g. "Referral Letter"'
                    onChange={(e) => this.handleEmrInputChange(dcId, e.target.value)}
                    onPressEnter={() => hasPendingChange && this.commitEmrCategory(entry)}
                    disabled={readOnly}
                  />
                  {hasPendingChange && !readOnly && (
                    <Button
                      type="primary"
                      size="small"
                      onClick={() => this.commitEmrCategory(entry)}
                    >
                      Save
                    </Button>
                  )}
                </div>
              );
            })()
          ) : (
            <div className="manage-categories__row-meta">
              Turn this AI Category on (above) to map it to an EMR Category.
            </div>
          )}
        </div>

        {enabled && this.renderListEditor({
          dcId,
          items: hints,
          open: hintsOpen,
          draft,
          readOnly,
          keyPrefix: 'hint',
          summary: (
            <span>
              <strong>Classification hints</strong>
              {' '}
              — only needed if a recurring document type keeps getting misclassified
              {hints.length > 0 && ` · ${hints.length} of 5 added`}
              .
            </span>
          ),
          toggleLabel: 'Add a hint',
          removeAriaLabel: 'Remove hint',
          placeholder: 'e.g. "TriWest Referral Authorizations are referrals"',
          maxCount: 5,
          maxLength: 100,
          maxedNoun: 'hints',
          onToggle: () => this.toggleHintsExpanded(dcId),
          onDraftChange: (value) => this.handleListDraftChange('hintDrafts', dcId, value),
          onSubmit: () => this.submitHint(entry),
          onRemove: (idx) => this.removeHint(entry, idx),
        })}

        {enabled && titlingEnabled && (
          <div className="manage-categories__hints-footer">
            <div className="manage-categories__hints-summary">
              <span>
                <strong>Custom titling</strong>
                {' '}
                — optional. Describe how you want documents in this category named,
                and Medsender&apos;s AI will format the title to match
                {titlingConfigured && ' · on'}
                .
              </span>
              {!readOnly && !titlingConfigured && (
                <button
                  type="button"
                  className="manage-categories__hints-toggle"
                  onClick={() => this.toggleTitlingExpanded(dcId)}
                >
                  {titlingOpen ? 'Cancel' : 'Set custom titling'}
                </button>
              )}
            </div>

            {titlingOpen && (
              <div className="manage-categories__titling">
                <div className="manage-categories__titling-help">
                  Give one example title, then describe what each part means. Use
                  the details the AI already extracts (study type, body part, side,
                  date, provider, facility). This describes a naming
                  {' '}
                  <em>format</em>
                  {' '}
                  — it can&apos;t track workflow notes or to-dos.
                </div>
                {dedupTitleCategory && (
                  <div className="manage-categories__titling-warning">
                    <WarningOutlined className="manage-categories__titling-warning-icon" />
                    <span>
                      Refill task de-duplication reads the prescription name and dose
                      from this title (e.g.
                      {' '}
                      <strong>Meloxicam 15mg</strong>
                      ).
                      {' '}
                      <strong>A custom title may affect de-duplication</strong>
                      {' '}
                      if those details are missing or cannot be parsed. Keep both the
                      medication name and dose in the generated title to preserve matching.
                    </span>
                  </div>
                )}
                {!readOnly ? (
                  <>
                    <div className="manage-categories__titling-field">
                      <div className="manage-categories__titling-label">Example title</div>
                      <Input
                        className="manage-categories__titling-input"
                        placeholder="RT KNEE MRI 07/09/26 - RIMI"
                        value={exampleValue}
                        maxLength={TITLE_EXAMPLE_MAX_LENGTH}
                        showCount
                        onChange={(e) => this.handleTitlingDraftChange(dcId, 'example', e.target.value)}
                      />
                    </div>
                    <div className="manage-categories__titling-field">
                      <div className="manage-categories__titling-label">What each part means</div>
                      <TextArea
                        className="manage-categories__titling-input"
                        autoSize={{ minRows: 3, maxRows: 6 }}
                        placeholder={
                          '<side (RT/LT)> <body part> <study type> '
                          + '<date of exam MM/DD/YY> - <imaging facility>'
                        }
                        value={descriptionValue}
                        maxLength={TITLE_INSTRUCTION_MAX_LENGTH}
                        showCount
                        onChange={(e) => this.handleTitlingDraftChange(dcId, 'description', e.target.value)}
                      />
                    </div>
                    <div className="manage-categories__titling-actions">
                      <Button
                        size="small"
                        type="primary"
                        disabled={!titlingDirty}
                        onClick={() => this.commitTitling(entry)}
                      >
                        Save
                      </Button>
                      {titlingConfigured && (
                        <Popconfirm
                          title="Remove custom titling for this category?"
                          okText="Remove"
                          onConfirm={() => this.removeTitling(entry)}
                        >
                          <Button size="small" type="link" danger>
                            Remove
                          </Button>
                        </Popconfirm>
                      )}
                    </div>
                  </>
                ) : (
                  <div className="manage-categories__titling-readonly">
                    {titlingConfigured ? (
                      <>
                        {savedTitleExample && (
                          <div>
                            <strong>Example:</strong>
                            {' '}
                            {savedTitleExample}
                          </div>
                        )}
                        {savedTitleInstruction && (
                          <div>
                            <strong>Means:</strong>
                            {' '}
                            {savedTitleInstruction}
                          </div>
                        )}
                      </>
                    ) : (
                      <em>Not set</em>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {enabled && titleClassificationEnabled && this.renderListEditor({
          dcId,
          items: titleOptions,
          open: titleOptionsOpen,
          draft: titleOptionDraft,
          readOnly,
          keyPrefix: 'title-option',
          summary: (
            <span>
              <strong>Title options</strong>
              {' '}
              — optional. The AI picks the document title (subject) from this
              list; manual uploads suggest these titles but still allow free text
              {titleOptions.length > 0 && ` · ${titleOptions.length} added`}
              .
            </span>
          ),
          toggleLabel: 'Add a title option',
          removeAriaLabel: 'Remove title option',
          placeholder: 'e.g. "CBC" or "Metabolic Panel"',
          maxCount: TITLE_OPTIONS_MAX_COUNT,
          maxLength: TITLE_OPTION_MAX_LENGTH,
          maxedNoun: 'title options',
          onToggle: () => this.toggleTitleOptionsExpanded(dcId),
          onDraftChange: (value) => this.handleListDraftChange('titleOptionDrafts', dcId, value),
          onSubmit: () => this.submitTitleOption(entry),
          onRemove: (idx) => this.removeTitleOption(entry, idx),
          saving: !!titleOptionsSaving[dcId],
        })}
      </div>
    );
  };

  renderLegacyCategories() {
    const { isLoading, isAdmin, headers } = this.props;
    const {
      canManageCategory,
      categories,
      currentPage,
      searchValue,
    } = this.state;
    const totalResults = headers ? headers.total_count : 1;
    const legacyColumns = [
      {
        title: (
          <Tooltip title="This is the dropdown list value of the category you want to be selected in your EMR during Auto-Upload. The category will be displayed in Medsender under the Document Title.">
            EMR Category Name
            <InfoCircleOutlined style={{ paddingLeft: 3, marginTop: 6, height: 10 }} />
          </Tooltip>
        ),
        dataIndex: 'emr_category',
        key: 'emr_category',
      },
      {
        title: (
          <Tooltip title="This is your label currently assigned to the corresponding EMR Category Name. If the Medsender AI assigns an AI tag to a record, this EMR Category Name will be automatically applied as well.">
            AI Tag (Label)
            <InfoCircleOutlined style={{ paddingLeft: 3, marginTop: 6, height: 10 }} />
          </Tooltip>
        ),
        dataIndex: 'ai_tag',
        key: 'ai_tag',
      },
      {
        title: '',
        key: 'edit',
        render: (_, category) => (
          <span>
            <Button onClick={() => this.openEditCategoryModal(category)} style={{ marginRight: 10 }}>
              Edit
            </Button>
            {((canManageCategory && isAdmin) || !canManageCategory) && (
              <Popconfirm
                title="Delete this Category? (This action is permanent and will remove any record reference for this category. This cannot be reversed.)"
                onConfirm={() => this.deleteCategory(category)}
              >
                <a>Delete</a>
              </Popconfirm>
            )}
          </span>
        ),
      },
    ];

    if (isLoading) return <Spin size="large" />;

    return (
      // eslint-disable-next-line object-curly-newline
      <div style={{ display: 'flex', flexDirection: 'column', width: '100%', padding: '16px 40px 40px', textAlign: 'left' }}>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 4, marginBottom: 16 }}>
          <h2 style={{ fontSize: 22, fontWeight: 600, color: '#181818', margin: 0, textAlign: 'left' }}>
            Manage Categories
          </h2>
          <div style={{ color: '#5c5c5c', fontSize: 13, lineHeight: 1.5, margin: 0, textAlign: 'left' }}>
            The categories Medsender&apos;s AI assigns to your incoming documents.
            <br />
            Each one maps to a category dropdown value in your EMR — selected
            automatically when auto-upload files the record.
            {' '}
            <Tooltip
              placement="bottom"
              title={(
                <div style={{ maxWidth: 340, lineHeight: 1.5 }}>
                  When a document arrives, the AI picks one of these categories. If
                  auto-upload is enabled on the receiving fax line, the matching EMR
                  Category is selected in your EMR&apos;s category dropdown so the
                  record files in the right place. Edit the EMR Category on a row to
                  change which value gets picked.
                </div>
              )}
            >
              <a style={{
                color: '#662c88',
                fontWeight: 500,
                cursor: 'help',
                textDecoration: 'none',
                borderBottom: '1px dotted currentColor',
              }}
              >
                How this works
              </a>
            </Tooltip>
          </div>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12 }}>
          {((canManageCategory && isAdmin) || !canManageCategory)
            ? (
              <Button
                icon={<PlusCircleOutlined />}
                type="primary"
                onClick={this.openEditCategoryModal}
              >
                Add Category
              </Button>
            )
            : (
              <span />
            )
          }
          <div>
            <Search
              placeholder="search categories"
              value={searchValue}
              onChange={(e) => this.setState({ searchValue: e.target.value })}
              onSearch={this.searchLegacyCategories}
              style={{ width: 200, height: 32, marginRight: 10 }}
            />
          </div>
        </div>
        <Table
          size="small"
          dataSource={categories}
          columns={legacyColumns}
          rowKey={(record) => record.id}
          bordered
          onChange={this.handleLegacyTableChange}
          pagination={{
            position: ['bottomCenter'],
            total: totalResults,
            current: currentPage,
          }}
        />
      </div>
    );
  }

  render() {
    const { catalog, isLoading, isAdmin } = this.props;
    const {
      canManageCategory, categories, catalogEnabled, expandedRowKeys,
    } = this.state;
    const allCatalogItems = catalog?.items || [];
    const filteredItems = this.filteredCatalog();

    if (catalogEnabled === null || isLoading) return <Spin size="large" />;
    if (!catalogEnabled) return this.renderLegacyCategories();

    const aiCategoryHeader = (
      <Tooltip title="An AI extraction category. When turned on, Medsender's AI will classify incoming documents into this category. Click a row for a fuller explanation.">
        <span>
          AI Category
          <InfoCircleOutlined style={{ marginLeft: 6, color: '#888' }} />
        </span>
      </Tooltip>
    );

    const emrCategoryHeader = (
      <Tooltip title="The exact value Medsender will select in your EMR's category dropdown when auto-uploading a document the AI classifies into the AI Category on the left.">
        <span>
          Your EMR Category
          <InfoCircleOutlined style={{ marginLeft: 6, color: '#888' }} />
        </span>
      </Tooltip>
    );

    // Wrap render output in a span with stopPropagation so the cell's click
    // doesn't bubble up and trigger row expansion (we want only the toggle
    // to flip the switch, not toggle expand/collapse).
    const stopRowClick = (e) => e.stopPropagation();

    const catalogColumns = [
      {
        title: '',
        key: 'enabled',
        width: 70,
        onCell: () => ({ onClick: stopRowClick }),
        render: (_, entry) => (
          <Tooltip title={entry.protected && entry.organization_state.enabled ? 'This category is required and cannot be disabled.' : ''}>
            <Switch
              role="switch"
              checked={entry.organization_state.enabled}
              disabled={(entry.protected && entry.organization_state.enabled) || (canManageCategory && !isAdmin)}
              onClick={() => this.handleToggleSupported(entry)}
            />
          </Tooltip>
        ),
      },
      {
        title: aiCategoryHeader,
        key: 'display_name',
        width: '55%',
        render: (_, entry) => this.renderRowName(entry),
      },
      {
        title: emrCategoryHeader,
        key: 'emr_category',
        render: (_, entry) => this.renderEmrReadonly(entry),
      },
    ];

    const customRows = (categories || []).filter((c) => c.ai_supported_document_category_id === null);

    return (
      <div className="manage-categories">
        <div className="manage-categories__header">
          <h2 className="manage-categories__title">Document categories</h2>
          <div className="manage-categories__subtitle">
            Choose how Medsender&apos;s AI files your incoming documents — turn on the
            categories you use, then label each with its EMR name.
            {' '}
            <Tooltip
              placement="bottom"
              title={(
                <div style={{ maxWidth: 320, lineHeight: 1.5 }}>
                  When the AI classifies an incoming document into a category you&apos;ve
                  turned on, Medsender selects the EMR Category you&apos;ve mapped here
                  during auto-upload. Click any row for details and to add
                  classification hints.
                </div>
              )}
            >
              <a className="manage-categories__learn-more">How this works</a>
            </Tooltip>
          </div>
        </div>

        {this.renderSummaryBar(allCatalogItems)}

        <Table
          className="manage-categories__table"
          size="middle"
          dataSource={filteredItems}
          columns={catalogColumns}
          rowKey="id"
          bordered={false}
          pagination={false}
          rowClassName={(entry) => (
            entry.organization_state.enabled ? '' : 'manage-categories__row-disabled'
          )}
          locale={{
            emptyText: (
              <div className="manage-categories__empty-state">
                No categories match your search.
              </div>
            ),
          }}
          expandable={{
            expandedRowKeys,
            onExpandedRowsChange: this.setExpandedRowKeys,
            expandRowByClick: true,
            expandIcon: ExpandToggle,
            expandedRowRender: (entry) => this.renderDetailPanel(entry),
            rowExpandable: () => true,
          }}
        />

        <Collapse className="manage-categories__legacy-section" defaultActiveKey={[]}>
          <Panel
            header={(
              <span className="manage-categories__legacy-header">
                Manual-only EMR categories
                {customRows.length > 0 && ` · ${customRows.length}`}
              </span>
            )}
            key="custom"
          >
            <p className="manage-categories__legacy-help">
              EMR categories that the AI never classifies into. Use these only for
              manual upload — for example a one-off category your EMR has that
              doesn&apos;t map to anything in the AI catalog above. Most orgs will
              never need these.
            </p>
            {((canManageCategory && isAdmin) || !canManageCategory) && (
              <Button
                size="small"
                icon={<PlusCircleOutlined />}
                onClick={() => this.openEditCategoryModal(null)}
                style={{ marginTop: 20, marginBottom: 16 }}
              >
                Add manual-only category
              </Button>
            )}
            {customRows.length === 0 ? (
              <div className="manage-categories__empty-state">
                No manual-only categories.
              </div>
            ) : (
              <Table
                size="small"
                dataSource={customRows}
                columns={[
                  {
                    title: (
                      <Tooltip title="The value Medsender selects in your EMR's category dropdown when uploading a record assigned to this category.">
                        <span>
                          EMR Category
                          <InfoCircleOutlined style={{ color: '#888' }} />
                        </span>
                      </Tooltip>
                    ),
                    dataIndex: 'emr_category',
                    key: 'emr_category',
                  },
                  {
                    title: (
                      <Tooltip title="Optional internal label. Not used by the AI catalog.">
                        <span>
                          Label
                          <InfoCircleOutlined style={{ color: '#888' }} />
                        </span>
                      </Tooltip>
                    ),
                    dataIndex: 'ai_tag',
                    key: 'ai_tag',
                  },
                  {
                    title: '',
                    key: 'edit',
                    render: (_, category) => (
                      <span>
                        <Button size="small" onClick={() => this.openEditCategoryModal(category)} style={{ marginRight: 10 }}>
                          Edit
                        </Button>
                        {((canManageCategory && isAdmin) || !canManageCategory) && (
                          <Popconfirm title="Delete this category?" onConfirm={() => this.deleteCategory(category)}>
                            <a>Delete</a>
                          </Popconfirm>
                        )}
                      </span>
                    ),
                  },
                ]}
                rowKey={(r) => r.id}
                bordered
                pagination={{ hideOnSinglePage: true, pageSize: 10 }}
              />
            )}
          </Panel>
        </Collapse>
      </div>
    );
  }
}

ManageCategories.defaultProps = {
  isLoading: false,
  data: [],
  catalog: {
    items: [],
    isLoading: false,
    error: null,
  },
  headers: {
    total_count: 1,
  },
  updateCustomCategory: () => Promise.resolve({}),
  addHint: () => Promise.resolve({}),
  removeHint: () => Promise.resolve({}),
  saveTitleInstruction: () => Promise.resolve({}),
  saveTitleOptions: () => Promise.resolve({}),
};

ManageCategories.propTypes = {
  isLoading: PropTypes.bool,
  data: PropTypes.array,
  getCategoriesByQuery: PropTypes.func.isRequired,
  catalog: PropTypes.shape({
    items: PropTypes.array.isRequired,
    isLoading: PropTypes.bool,
    error: PropTypes.string,
  }),
  headers: PropTypes.shape({
    total_count: PropTypes.number,
  }),
  fetchCategoryCatalog: PropTypes.func.isRequired,
  enableSupported: PropTypes.func.isRequired,
  disableSupported: PropTypes.func.isRequired,
  updateCustomCategory: PropTypes.func,
  addHint: PropTypes.func,
  removeHint: PropTypes.func,
  saveTitleInstruction: PropTypes.func,
  saveTitleOptions: PropTypes.func,
  deleteCategory: PropTypes.func.isRequired,
  openModal: PropTypes.func.isRequired,
  getUserInfo: PropTypes.func.isRequired,
  isAdmin: PropTypes.bool.isRequired,
};

export default connect(
  (state) => ({
    data: state.categories.data,
    isLoading: state.categories.isLoading,
    catalog: state.categories.catalog,
    headers: state.categories.headers,
    isAdmin: state.userProfile.isAdmin,
  }),
  {
    fetchCategoryCatalog: getCategoryCatalog,
    enableSupported: enableSupportedCategory,
    disableSupported: disableSupportedCategory,
    updateCustomCategory: updateCategory,
    addHint: addClassificationHint,
    removeHint: removeClassificationHint,
    saveTitleInstruction: setTitleInstruction,
    saveTitleOptions: setTitleOptions,
  },
)(ManageCategories);
